import { useRef, useState } from "react";
import { Download, ImagePlus, Loader2, Trash2, PenLine, Upload } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, studio, apiError, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";
import { SignaturePad } from "@/components/studio/SignaturePad";
import { mediaUrl } from "@/lib/photos";

const THEMES = [
  { value: "navy", label: "Bleu nuit", dark: "#0F2645", primary: "#3A5CC8" },
  { value: "noir_or", label: "Noir & or", dark: "#141416", primary: "#B9913E" },
  { value: "vert_atelier", label: "Vert atelier", dark: "#0E2A20", primary: "#168C5A" },
  { value: "gris_industriel", label: "Gris industriel", dark: "#212831", primary: "#6E7A8C" },
  { value: "personnalise", label: "Ma couleur", dark: "#334155", primary: "#3A5CC8" },
];

const LAYOUTS = [
  { value: "classique", label: "Classique", hint: "Titres et en-têtes de tableau dans votre couleur pleine, plus sobre." },
  { value: "moderne", label: "Moderne", hint: "Titre et encarts en teinte douce, modes de paiement et totaux côte à côte." },
  { value: "compact", label: "Compact", hint: "Plus dense : idéal pour les longs devis." },
];

const FONTS = [
  { value: "moderne", label: "Moderne (sans empattement)" },
  { value: "classique", label: "Classique (avec empattements)" },
  { value: "technique", label: "Technique (chasse fixe)" },
];

export const SignatureBlock = ({ profile, setProfile }) => {
  const padRef = useRef(null);
  const fileRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async (data) => {
    setSaving(true);
    try {
      const next = { ...profile, signature_data: data };
      await studio.put("/profile", { ...next, quote_validity_days: Number(next.quote_validity_days) || 30 });
      setProfile(next);
      setDrawing(false);
      toast.success(data ? "Signature enregistrée : elle apparaîtra sur vos devis, rapports et contrats." : "Signature retirée.");
    } catch (e) {
      toast.error(apiError(e, "Enregistrement impossible."));
    } finally {
      setSaving(false);
    }
  };

  const fromFile = (file) => {
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      const scale = Math.min(1, 900 / img.width);
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      save(c.toDataURL("image/png"));
    };
    img.src = URL.createObjectURL(file);
  };

  return (
    <div data-testid="profile-signature" className="mt-5 rounded-xl border border-[#E2E7F7] bg-white p-4">
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Signature de l'entreprise</p>
      <p className="mt-1 text-xs text-[#5C6A87]">Apposée dans le cadre « Signature de l'entreprise » de vos devis, contrats et rapports PDF.</p>
      {drawing ? (
        <div className="mt-3">
          <SignaturePad ref={padRef} testId="profile-signature-pad" height={140} />
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" data-testid="profile-signature-save" disabled={saving} onClick={() => { const d = padRef.current?.toDataURL(); if (!d) return toast.error("Signez d'abord dans le cadre."); save(d); }} className={btnPrimary}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} Enregistrer</button>
            <button type="button" onClick={() => padRef.current?.clear()} className={btnGhost}>Effacer</button>
            <button type="button" onClick={() => setDrawing(false)} className={btnGhost}>Annuler</button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <div className="flex h-20 w-44 items-center justify-center rounded-xl border border-dashed border-[#C9D4F4] bg-[#F6F8FD] p-2">
            {profile.signature_data ? <img data-testid="profile-signature-preview" src={profile.signature_data} alt="Signature" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-[#93A0BC]">Aucune signature</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="profile-signature-draw" onClick={() => setDrawing(true)} className={btnGhost}><PenLine className="h-4 w-4" /> Signer au doigt</button>
            <button type="button" data-testid="profile-signature-upload" onClick={() => fileRef.current?.click()} className={btnGhost}><Upload className="h-4 w-4" /> Importer une image</button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => fromFile(e.target.files?.[0])} />
            {profile.signature_data && <button type="button" data-testid="profile-signature-remove" onClick={() => save("")} className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:underline"><Trash2 className="w-3 h-3" /> Retirer</button>}
          </div>
        </div>
      )}
    </div>
  );
};

export const DocumentBranding = ({ profile, setProfile, hideFooter = false }) => {
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileRef = useRef(null);

  const set = (key, value) => setProfile({ ...profile, [key]: value });

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/media/upload`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("devisto_token")}` },
      });
      const next = { ...profile, logo_media_id: data.id };
      setProfile(next);
      await studio.put("/profile", { ...next, quote_validity_days: Number(next.quote_validity_days) || 30 });
      toast.success("Logo enregistré.");
    } catch (e) {
      toast.error(apiError(e, "Téléversement impossible."));
    } finally {
      setUploading(false);
    }
  };

  const openPreview = async () => {
    setPreviewing(true);
    try {
      await studio.put("/profile", { ...profile, quote_validity_days: Number(profile.quote_validity_days) || 30 });
      const res = await studio.get("/documents/preview", { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) {
      toast.error(apiError(e, "Aperçu impossible."));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div data-testid="profile-branding" className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
      <p className="text-sm font-semibold text-[#0F2645]">Personnalisation de mes documents</p>
      <p className="mt-1 text-xs text-[#5C6A87] leading-relaxed">
        Logo, couleurs, police et mise en page appliqués à vos devis, rapports de visite et rapports d'intervention.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          {profile.logo_media_id ? (
            <img
              data-testid="branding-logo-preview"
              src={mediaUrl(profile.logo_media_id)}
              alt="Logo"
              className="h-14 w-auto max-w-[10rem] rounded-lg border border-[#E2E7F7] bg-white object-contain p-1.5"
            />
          ) : (
            <div className="h-14 w-32 rounded-lg border border-dashed border-[#E2E7F7] bg-white grid place-items-center text-[11px] text-[#93A0BC]">
              Aucun logo
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" hidden data-testid="branding-logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            <button
              type="button"
              data-testid="branding-logo-btn"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E2E7F7] bg-white px-4 py-2 text-xs font-semibold text-[#0F2645] hover:border-[#3A5CC8] transition-colors"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
              {profile.logo_media_id ? "Changer le logo" : "Ajouter mon logo"}
            </button>
            {profile.logo_media_id && (
              <button
                type="button"
                data-testid="branding-logo-remove"
                onClick={() => set("logo_media_id", "")}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#93A0BC] hover:text-red-600 transition-colors"
              >
                <Trash2 className="w-3 h-3" /> Retirer
              </button>
            )}
          </div>
        </div>
      </div>

      <SignatureBlock profile={profile} setProfile={setProfile} />

      <p className="mt-5 text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Thème</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {THEMES.map((theme) => (
          <button
            key={theme.value}
            type="button"
            data-testid={`branding-theme-${theme.value}`}
            onClick={() => set("doc_theme", theme.value)}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
              (profile.doc_theme || "navy") === theme.value ? "border-[#3A5CC8] bg-white text-[#3A5CC8]" : "border-[#E2E7F7] bg-white text-[#5C6A87]"
            }`}
          >
            <span className="flex h-5 w-9 overflow-hidden rounded-md border border-[#E2E7F7]">
              <span className="w-1/2" style={{ background: theme.value === "personnalise" ? profile.doc_color || "#3A5CC8" : theme.dark }} />
              <span className="w-1/2" style={{ background: theme.value === "personnalise" ? profile.doc_color || "#3A5CC8" : theme.primary }} />
            </span>
            {theme.label}
          </button>
        ))}
        {(profile.doc_theme || "navy") === "personnalise" && (
          <input
            data-testid="branding-color"
            type="color"
            value={profile.doc_color || "#3A5CC8"}
            onChange={(e) => set("doc_color", e.target.value)}
            className="h-9 w-14 cursor-pointer rounded-lg border border-[#E2E7F7] bg-white p-1"
          />
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Mise en page</p>
          <select data-testid="branding-layout" className={`${inputCls} mt-2`} value={profile.doc_layout || "moderne"} onChange={(e) => set("doc_layout", e.target.value)}>
            {LAYOUTS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
          <p className="mt-1 text-[11px] text-[#93A0BC]">
            {LAYOUTS.find((l) => l.value === (profile.doc_layout || "moderne"))?.hint}
          </p>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Police</p>
          <select data-testid="branding-font" className={`${inputCls} mt-2`} value={profile.doc_font || "moderne"} onChange={(e) => set("doc_font", e.target.value)}>
            {FONTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
      </div>

      {!hideFooter && (
        <input
          data-testid="branding-footer"
          className={`${inputCls} mt-3`}
          placeholder="Pied de page des documents (ex. : Chauffage Marc SPRL — IBAN BE00 0000 0000 0000 — merci de votre confiance)"
          value={profile.doc_footer || ""}
          onChange={(e) => set("doc_footer", e.target.value)}
        />
      )}

      <label className="mt-3 flex items-start gap-3 rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid="branding-email-logo"
          className="mt-0.5 h-4 w-4 accent-[#3A5CC8]"
          checked={profile.email_logo !== false}
          onChange={(e) => set("email_logo", e.target.checked)}
        />
        <span className="text-xs text-[#5C6A87] leading-relaxed">
          <span className="block text-sm font-semibold text-[#0F2645]">Afficher mon logo dans les emails clients</span>
          Rappels de visite, envoi de devis et rapports : votre logo apparaît en tête de l'email.
        </span>
      </label>

      <button
        type="button"
        data-testid="branding-preview-btn"
        disabled={previewing}
        onClick={openPreview}
        className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[#E2E7F7] bg-white px-5 py-2.5 text-xs font-semibold text-[#0F2645] hover:border-[#3A5CC8] transition-colors"
      >
        {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Aperçu d'un devis
      </button>
    </div>
  );
};
